/** Add/remove chip list — Enter/comma to add, click × to remove.
 *
 * Shared by the fleet ArrayField inputs (hidden statuses, route-unassign events)
 * and the webhook email recipients editor.
 */
import { Autocomplete, Box, Chip, TextField, Typography } from '@mui/material';
import { useState } from 'react';

interface Props {
  label: string;
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  helperText?: string;
  /** Optional fixed suggestions offered in a dropdown (e.g. vehicle statuses). */
  options?: string[];
  transform?: (raw: string) => string;
  disabled?: boolean;
}

export function ChipListField({
  label,
  values,
  onChange,
  placeholder,
  helperText,
  options,
  transform,
  disabled,
}: Props) {
  const [input, setInput] = useState('');

  function add(raw: string) {
    const v = (transform ? transform(raw) : raw).trim();
    if (v && !values.includes(v)) onChange([...values, v]);
    setInput('');
  }

  function remove(v: string) {
    onChange(values.filter((x) => x !== v));
  }

  if (options && options.length > 0) {
    return (
      <Autocomplete
        multiple
        freeSolo
        disabled={disabled}
        options={options}
        value={values}
        onChange={(_, v) => onChange(v as string[])}
        renderInput={(params) => (
          <TextField {...params} label={label} placeholder={placeholder} helperText={helperText} />
        )}
      />
    );
  }

  return (
    <Box>
      <TextField
        fullWidth
        size="small"
        label={label}
        placeholder={placeholder}
        helperText={helperText}
        value={input}
        disabled={disabled}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            add(input);
          }
        }}
      />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
        {values.length === 0 && (
          <Typography variant="caption" color="text.disabled">
            None
          </Typography>
        )}
        {values.map((v) => (
          <Chip
            key={v}
            label={v}
            size="small"
            onDelete={disabled ? undefined : () => remove(v)}
          />
        ))}
      </Box>
    </Box>
  );
}
